import { actions, TableActionType } from './TableAction';
import { TableRegions } from './TableRegions';
const reduce = (previousState, action) => {
  switch (action.type) {
    case TableActionType.SelectRow:
      {
        const {
          rowKey,
          getRowIndex,
          index,
          clearPrevious,
          rangeSelection,
          selectionType,
          selected,
          hideSelectAll,
          columnHeadersVisible,
          areRowsVisible,
          isRowDisabled,
          isRowReadOnly,
          selectionStart,
          interactive
        } = action.payload;
        const rowCount = action.payload.rowCount ?? 0;
        const selectedRowStates = new Set(clearPrevious === true ? null : previousState.selectedRows);
        const key = rowKey(index);
        const getRowKey = row => typeof row === 'string' ? row : rowKey(row);
        const isDisabledOrReadOnly = rowKey => !!isRowDisabled?.(rowKey) || !!isRowReadOnly?.(rowKey);
        const addNonDisabledSelection = (row, previousSelectedRows, previousSelectAll) => {
          const rowKey = getRowKey(row);
          if (interactive && isRowDisabled?.(rowKey)) {
            return {
              ...previousState
            };
          }
          if (!isRowDisabled?.(rowKey) && key !== rowKey) {
            selectedRowStates.add(rowKey);
          } else {
            const selected = previousSelectedRows?.has(rowKey);
            (selected || previousSelectAll) && key !== rowKey && selectedRowStates.add(rowKey);
          }
        };
        const addSelection = (row, previousSelectedRows, previousSelectAll) => {
          const rowKey = getRowKey(row);
          if (interactive && isDisabledOrReadOnly(rowKey)) {
            return {
              ...previousState
            };
          }
          if (!isDisabledOrReadOnly(rowKey)) selectedRowStates.add(rowKey);else {
            const selected = previousSelectedRows?.has(rowKey);
            (selected || previousSelectAll) && selectedRowStates.add(rowKey);
          }
        };
        const deleteSelection = row => {
          const rowKey = getRowKey(row);
          if (!isDisabledOrReadOnly(rowKey)) selectedRowStates.delete(rowKey);
        };
        let anchor;
        if (rangeSelection) {
          let first = selectionStart ?? previousState.lastSelectedIndex;
          if (first === undefined && previousState.selectedRows && previousState.selectedRows.size > 0) {
            const lastSelectedKey = Array.from(previousState.selectedRows).pop();
            if (getRowIndex && lastSelectedKey !== undefined) first = getRowIndex(lastSelectedKey);
          }
          const current = index;
          // How the previous selection was made matters for range selections (ctrl+click vs shift+click)
          // if shift+click, it should use the lastSelectedRow as an anchor for the subsequent shift+click
          const previousRangeSelection = previousState.rangeSelection;
          anchor = previousRangeSelection && previousState.firstRangeSelection !== undefined ? previousState.firstRangeSelection : first;
          if (anchor !== undefined) {
            if (clearPrevious === 'selectionRange' && previousState.lastSelectedIndex !== undefined) {
              const min = Math.min(anchor, previousState.lastSelectedIndex);
              const max = Math.max(anchor, previousState.lastSelectedIndex);
              for (let rowIndex = min; rowIndex <= max; rowIndex++) {
                deleteSelection(rowIndex);
              }
            }
            const min = Math.min(anchor, current);
            const max = Math.max(anchor, current);
            for (let rowIndex = min; rowIndex <= max; rowIndex++) {
              addSelection(rowIndex, previousState.selectedRows, previousState.selectAll);
            }
          }
        } else {
          if (previousState.selectAll === true && clearPrevious !== true && rowCount) {
            for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
              addNonDisabledSelection(rowIndex, previousState.selectedRows, previousState.selectAll);
            }
            deleteSelection(key);
          } else {
            !selected ? deleteSelection(key) : addSelection(key, previousState.selectedRows, previousState.selectAll);
            // when there are multiple selections and the row is selected on the cell, that row remains selected unless
            // the shift/ctrl+click.
            if (selectionType === 'cell' && clearPrevious === true && previousState.selectedRows && previousState.selectedRows?.size > 1 || selectionType === 'cell' && clearPrevious === true && previousState.selectAll === true && rowCount > 1) {
              addSelection(key, previousState.selectedRows, previousState.selectAll);
            }
          }
        }
        let first = selectionStart ?? previousState.lastSelectedIndex;
        if (first === undefined && previousState.selectedRows) {
          const lastSelectedKey = Array.from(previousState.selectedRows).pop();
          if (getRowIndex && lastSelectedKey !== undefined) first = getRowIndex(lastSelectedKey);
        }
        const selectedRowsValidated = !areRowsVisible ? true : areRowsVisible(selectedRowStates);
        let selectAllState;
        if (selectedRowsValidated && hideSelectAll !== true && rowCount !== 0 && columnHeadersVisible !== false && selectedRowStates.size >= rowCount) {
          selectAllState = true;
        } else if (selectedRowStates.size > 0) {
          selectAllState = 'mixed';
        } else {
          selectAllState = false;
        }
        return {
          ...previousState,
          selectedRows: selectedRowStates,
          selectAll: selectAllState,
          lastSelectedIndex: index,
          rangeSelection,
          selectionMetadata: {
            key,
            state: selected
          },
          lastSelectedRowKey: key,
          lastSelectedRowState: selected,
          firstRangeSelection: !previousState.rangeSelection ? first : previousState.firstRangeSelection,
          selectionAnchor: anchor
        };
      }
    case TableActionType.SelectAll:
      const {
        isRowReadOnly,
        selectAll,
        initialSelectedRows
      } = action.payload;
      let selectedRows;
      if (isRowReadOnly && selectAll !== true) {
        const previousSelectedRows = initialSelectedRows ?? previousState.selectedRows;
        previousSelectedRows?.forEach(key => {
          if (isRowReadOnly(key)) {
            if (!selectedRows) selectedRows = new Set();
            selectedRows?.add(key);
          }
        });
      }
      return {
        ...previousState,
        selectedRows,
        lastSelectedRowKey: undefined,
        lastSelectedRowState: undefined,
        lastSelectedIndex: undefined,
        selectAll,
        selectionMetadata: {}
      };
    case TableActionType.SelectRows:
      {
        const {
          areRowsVisible,
          selectedRows: newSelectedRow,
          hideSelectAll,
          columnHeadersVisible,
          rowCount,
          isRowDisabled,
          isRowReadOnly,
          initialSelectedRows,
          interactive
        } = action.payload;
        const selectedRows = new Set();
        newSelectedRow?.forEach(key => {
          if ((isRowDisabled?.(key) || isRowReadOnly?.(key)) && (previousState.selectedRows?.has(key) || previousState.selectAll === true || initialSelectedRows?.has(key))) {
            selectedRows.add(key);
          } else if (!isRowDisabled?.(key) || !interactive) selectedRows.add(key);
        });
        const selectedRowsValidated = !areRowsVisible || !selectedRows ? true : areRowsVisible(selectedRows);
        const selectAllState = () => {
          if (selectedRowsValidated && selectedRows && hideSelectAll !== true && columnHeadersVisible !== false && selectedRows.size === rowCount && selectedRows.size > 0) return true;
          return selectedRows && selectedRows.size > 0 ? 'mixed' : false;
        };
        return {
          ...previousState,
          selectedRows: new Set(selectedRows),
          selectAll: selectAllState(),
          selectionMetadata: {}
        };
      }
    case TableActionType.FocusCell:
      return {
        ...previousState,
        lastFocusedCell: previousState.initialFocus,
        lastFocusedDataCell: action.payload.focusedCell?.region === TableRegions.Data || action.payload.focusedCell?.region === TableRegions.RowHeader ? action.payload.focusedCell : previousState.lastFocusedDataCell,
        initialFocus: action.payload.focusedCell,
        activeCell: action.payload.focusedCell
      };
    case TableActionType.ActiveCell:
      return {
        ...previousState,
        activeCell: action.payload.cell
      };
    case TableActionType.PressedCell:
      return {
        ...previousState,
        pressedCell: action.payload.cell,
        pressEntireRow: action.payload.pressRow ?? true
      };
    case TableActionType.FocusIndicator:
      {
        return {
          ...previousState,
          focusIndicator: action.payload.focusIndicator
        };
      }
    case TableActionType.FrozenColumn:
      {
        const {
          count
        } = action.payload;
        return {
          ...previousState,
          frozenColumnCount: count
        };
      }
    case TableActionType.ResizeColumn:
      {
        const resizedColumns = previousState.resizedColumns ? new Map(previousState.resizedColumns) : new Map();
        if (action.payload.columnKey && (resizedColumns.has(action.payload.columnKey) || action.payload.width === undefined)) {
          resizedColumns.delete(action.payload.columnKey);
        }
        if (action.payload.width !== undefined && action.payload.columnKey) resizedColumns.set(action.payload.columnKey, action.payload.width);
        return {
          ...previousState,
          resizedColumns
        };
      }
    case TableActionType.ClearResizedColumns:
      {
        return {
          ...previousState,
          resizedColumns: undefined
        };
      }
    case TableActionType.FreezeColumn:
      {
        const {
          unfreeze,
          columnKey
        } = action.payload;
        const orderedColumnKeys = [...(action.payload.orderedColumnKeys || [])];
        const freezeCount = previousState.frozenColumnCount || 0;
        const index = orderedColumnKeys.findIndex(key => key === columnKey);
        if (index > -1) orderedColumnKeys.splice(index, 1);
        const insertAt = unfreeze ? freezeCount - 1 : freezeCount;
        orderedColumnKeys.splice(insertAt, 0, columnKey);
        return {
          ...previousState,
          frozenColumnCount: unfreeze ? freezeCount - 1 : freezeCount + 1,
          orderedColumnKeys
        };
      }
    case TableActionType.OrderedColumnKeys:
      {
        const previousKeys = previousState.orderedColumnKeys ?? [];
        const previousFrozenKeys = previousKeys.slice(0, previousState.frozenColumnCount);
        const newFrozenKeys = action.payload.orderedKeys.slice(0, previousState.frozenColumnCount);
        const allPreviousKeysStillFrozen = previousFrozenKeys.every(key => newFrozenKeys.includes(key));
        const nextFrozenColumnCount = allPreviousKeysStillFrozen ? previousState.frozenColumnCount : previousKeys.findIndex((previousKey, i) => newFrozenKeys[i] !== previousKey) || 0;
        const orderedKeys = action.payload.orderedKeys;
        return {
          ...previousState,
          frozenColumnCount: nextFrozenColumnCount,
          orderedColumnKeys: [...orderedKeys]
        };
      }
    case TableActionType.ScrollPosition:
      {
        return {
          ...previousState,
          scrollPosition: action.payload,
          focusIndicator: 'visible',
          initialFocus: action.payload.cell
        };
      }
    case TableActionType.ClearMeasurementCache:
      {
        return {
          ...previousState,
          clearMeasurementsStartingColumnIndex: action.payload.startColumnIndex,
          clearMeasurementsStartingRowIndex: action.payload.startRowIndex
        };
      }
    case TableActionType.PartiallySelectedRows:
      {
        return {
          ...previousState,
          partiallySelectedRows: action.payload.partiallySelected
        };
      }
    default:
      return previousState;
  }
};
const init = (selectedRows, selectAll, focusedCell, frozenColumnCount, orderedColumnKeys, resizedColumns, partiallySelectedRows, scrollPosition) => {
  return {
    selectedRows,
    selectAll: selectAll || (selectedRows && selectedRows.size > 0 ? 'mixed' : false),
    initialFocus: focusedCell,
    rangeSelection: false,
    frozenColumnCount,
    orderedColumnKeys,
    resizedColumns,
    partiallySelectedRows,
    scrollPosition
  };
};
export const TableState = {
  init,
  reduce,
  actions
};
//# sourceMappingURL=TableState.js.map